import {Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {AbstractControl, ReactiveFormsModule, UntypedFormBuilder, ValidationErrors, Validators} from '@angular/forms';
import {ProgressComponent} from '../progress/progress.component';
import {Facility} from '../../models/facility';
import {catchError, map, switchMap, take, tap} from 'rxjs/operators';
import {ActivatedRoute, Router} from '@angular/router';
import {from, Observable, Subscription, throwError, timer} from 'rxjs';
import {Location} from '@angular/common';
import {MatSnackBar} from '@angular/material/snack-bar';
import {BreadCrumb} from '../../models/breadcrumb';
import {DialogService} from '../../services/dialog.service';
import {CanComponentDeactivate} from '../../guards/can-deactive.guard';
import {
    addDoc,
    collection,
    doc,
    docData,
    DocumentData,
    DocumentReference,
    Firestore,
    getDocs,
    query,
    serverTimestamp,
    updateDoc,
    where
} from '@angular/fire/firestore';
import {Auth, authState} from '@angular/fire/auth';
import {BreadcrumbsComponent} from '../breadcrumbs/breadcrumbs.component';
import {MatCard, MatCardContent, MatCardHeader, MatCardSubtitle, MatCardTitle} from '@angular/material/card';
import {MatError, MatInput} from '@angular/material/input';
import {MatFormField} from '@angular/material/form-field';
import {MatButton} from '@angular/material/button';
import {MatOption, MatSelect} from '@angular/material/select';

@Component({
    selector: 'app-facility-form',
    templateUrl: './facility-form.component.html',
    styleUrls: ['./facility-form.component.css'],
    imports: [
        ProgressComponent,
        BreadcrumbsComponent,
        MatCard,
        MatCardTitle,
        MatCardHeader,
        MatCardContent,
        ReactiveFormsModule,
        MatFormField,
        MatError,
        MatInput,
        MatSelect,
        MatOption,
        MatButton
    ],
    standalone: true
})
export class FacilityFormComponent implements OnInit, OnDestroy, CanComponentDeactivate {

    @ViewChild(ProgressComponent, {static: true}) progress: ProgressComponent;

    facilityId: string;
    accountId: string;
    userSubscription: Subscription;
    authStateSubscription: Subscription;
    facility: Observable<DocumentData>;
    facilitySubscription: Subscription;
    breadcrumbs: BreadCrumb[];
    facilityForm = this.formBuilder.group({
        name: ['', Validators.required, this.validateUniqueName.bind(this)],
        type: ['', Validators.required],
        address: ['', Validators.required],
    });
    types = [{name: 'BRANCH', value: 1}, {name: 'CUSTOMER', value: 2}];
    private facilityDocument: DocumentReference;

    constructor(
        private formBuilder: UntypedFormBuilder,
        private firestore: Firestore,
        private router: Router,
        private activeRoute: ActivatedRoute,
        private location: Location,
        private snackBar: MatSnackBar,
        private fireAuth: Auth,
        private dialogService: DialogService) {
    }

    ngOnInit() {
        this.breadcrumbs = [
            {url: ['/'], title: 'Accounts'},
            {url: null, title: 'Facilities'},
            {url: null, title: 'New Facility'},
        ];
        this.activeRoute.paramMap.subscribe(
            params => {
                this.accountId = params.get('accountId');
                this.facilityId = params.get('facilityId');
                if (this.facilityId) {
                    this.fetchFacility();
                }
                if (!this.accountId) {
                    this.progress.setVisible(true);
                    this.authStateSubscription = authState(this.fireAuth).pipe(
                        take(1),
                        catchError(err => {
                            this.progress.setVisible(false);
                            return throwError(() => new Error(err));
                        })
                    ).subscribe(user => {
                        const userDocRef = doc(this.firestore, 'users', user.uid);
                        this.userSubscription = docData(userDocRef).pipe(
                            catchError(err => {
                                this.progress.setVisible(false);
                                return throwError(() => err);
                            })
                        ).subscribe(result => {
                            this.accountId = result.accountId;
                            this.progress.setVisible(false);
                        });
                    });
                }
            }
        );
    }

    canDeactivate(): Observable<boolean> | Promise<boolean> | boolean {
        if (this.facilityForm.dirty) {
            const confirm: Observable<boolean> = this.dialogService.confirm('Discard changes for Facility?');
            confirm.subscribe(conf => {
                if (!conf) {
                    this.location.go(this.router.url);
                    return false;
                }
            });
            return confirm;
        }
        return true;
    }

    validateUniqueName(control: AbstractControl): Observable<ValidationErrors | null> {
        return timer(500).pipe(
            switchMap(() => {
                const facilitiesColRef = collection(this.firestore, 'facilities');
                const facilitiesQuery = query(
                    facilitiesColRef,
                    where('name', '==', control.value)
                );
                return from(getDocs(facilitiesQuery)).pipe(
                    map(snapshot => {
                        if (snapshot.empty) {
                            return null;
                        }
                        if (this.facilityId) {
                            const duplicate = snapshot.docs.some(docSnap => docSnap.id !== this.facilityId);
                            return duplicate ? {duplicate: true} : null;
                        } else {
                            return {duplicate: true};
                        }
                    })
                );
            })
        );
    }

    async submit() {
        this.progress.setVisible(true);
        if (this.facilityId) {
            await updateDoc(this.facilityDocument, {...this.facilityForm.value, ...{accountId: this.accountId}});
        } else {
            const model = {} as Facility;
            model.accountId = this.accountId;
            model.created = serverTimestamp();
            await addDoc(collection(this.firestore, 'facilities'), {...model, ...this.facilityForm.value});
        }
        this.progress.setVisible(false);
        this.snackBar.open(`Facility ${this.facilityId ? 'updated' : 'saved'}`, null, {duration: 3000});
        this.facilityForm.markAsPristine();
        this.location.back();
    }

    fetchFacility() {
        this.progress.setVisible(true);
        this.facilityDocument = doc(this.firestore, 'facilities', this.facilityId);
        this.facility = docData(this.facilityDocument);
        this.facility.pipe(
            tap(data => {
                this.breadcrumbs[2].title = 'Edit Facility: ' + data.name;
                this.facilityForm.patchValue(data);
                this.facilityForm.get('name').markAsTouched();
                this.progress.setVisible(false);
            })
        ).subscribe();
    }

    ngOnDestroy(): void {
        if (this.facilitySubscription != null) {
            this.facilitySubscription.unsubscribe();
        }
        if (this.authStateSubscription) {
            this.authStateSubscription.unsubscribe();
        }
        if (this.userSubscription) {
            this.userSubscription.unsubscribe();
        }
    }
}
